// Hit-Parade-only decor: two colorful "flying cannons" perched at the top
// of the mountain on either side of the path. They bob in the air, aim
// down-slope at the runner, and periodically fire a muzzle flash + recoil
// kick to suggest they're the source of the rolling balls.
//
// Also exports a moving start platform that the player can ride from the
// starting line into the climb.

import * as THREE from "three";
import { SoftBox } from "./roundedBoxes";
import type { Path } from "./Path";

/** Snapshot of a platform's current state — published by `tick()` each frame
 *  for use by Game-side collision (player carry, bouncing-ball impacts, etc). */
export interface PlatformState {
  /** Arc-length on the path of the platform center. */
  s: number;
  /** Lateral centre offset from the path spine. Defaults to 0 (centred).
   *  Used by space-ship to run two laterally-offset platforms per gap. */
  centerL?: number;
  /** World-y of the platform's top surface. */
  topY: number;
  /** Half-extents on the (s, l) plane. */
  halfDepth: number;
  halfWidth: number;
  /** Vertical half-extent — used by sphere-vs-AABB collision. */
  halfHeight: number;
  /** Current forward velocity along the path (m/s). */
  velocityS: number;
}

/** Public handle returned by buildHitParadeStartPlatform for Game integration. */
export interface MovingPlatform {
  /** Mesh group added to the scene. */
  group: THREE.Group;
  /** Per-frame update. Returns current platform state for collision use. */
  tick: (dt: number) => PlatformState;
}

interface Cannon {
  group: THREE.Group;
  basePos: THREE.Vector3;
  baseYaw: number;
  cannon: THREE.Group;
  flash: THREE.Mesh;
  flashMat: THREE.MeshBasicMaterial;
  nextFireAt: number;
  recoilAt: number;
  bobPhase: number;
  side: 1 | -1;
}

const COLORS = [0xff5fa7, 0x6bd0ff]; // left = pink, right = cyan
const TRIM = 0xffd166;
const BARREL = 0x9b5cf6;

function makeCannon(side: 1 | -1): { group: THREE.Group; cannon: THREE.Group; flash: THREE.Mesh; flashMat: THREE.MeshBasicMaterial } {
  const group = new THREE.Group();

  // Floating platform: candy disc + hover-glow.
  const platformColor = side === -1 ? COLORS[0]! : COLORS[1]!;
  const platform = new THREE.Mesh(
    new THREE.CylinderGeometry(2.4, 2.7, 0.55, 24),
    new THREE.MeshStandardMaterial({ color: platformColor, roughness: 0.4, metalness: 0.1 }),
  );
  platform.castShadow = true;
  platform.receiveShadow = true;
  group.add(platform);

  const trim = new THREE.Mesh(
    new THREE.TorusGeometry(2.5, 0.18, 12, 32),
    new THREE.MeshStandardMaterial({ color: TRIM, roughness: 0.35, metalness: 0.45 }),
  );
  trim.rotation.x = Math.PI / 2;
  trim.position.y = -0.05;
  group.add(trim);

  // Cute little wings (Fall Guys-ish — cartoon, not realistic).
  for (const wx of [-1, 1] as const) {
    const wing = new THREE.Mesh(
      new SoftBox(0.7, 0.18, 1.4),
      new THREE.MeshStandardMaterial({ color: TRIM, roughness: 0.35 }),
    );
    wing.position.set(wx * 2.6, 0.1, 0);
    wing.rotation.z = wx * 0.4;
    group.add(wing);
  }

  // Hover jet under the platform — yellow glow puff.
  const jet = new THREE.Mesh(
    new THREE.ConeGeometry(1.1, 1.4, 16, 1, true),
    new THREE.MeshBasicMaterial({ color: 0xffe580, transparent: true, opacity: 0.55 }),
  );
  jet.rotation.x = Math.PI;
  jet.position.y = -0.95;
  group.add(jet);

  // Cannon barrel mounted on top, swivels (we yaw the entire `cannon` group).
  const cannon = new THREE.Group();
  cannon.position.y = 0.55;
  const barrel = new THREE.Mesh(
    new THREE.CylinderGeometry(0.55, 0.7, 2.6, 24),
    new THREE.MeshStandardMaterial({ color: BARREL, roughness: 0.4, metalness: 0.18 }),
  );
  barrel.rotation.z = Math.PI / 2;
  barrel.position.x = 1.05;
  barrel.castShadow = true;
  cannon.add(barrel);

  const muzzle = new THREE.Mesh(
    new THREE.CylinderGeometry(0.72, 0.78, 0.32, 24),
    new THREE.MeshStandardMaterial({ color: TRIM, roughness: 0.35, metalness: 0.5 }),
  );
  muzzle.rotation.z = Math.PI / 2;
  muzzle.position.x = 2.45;
  cannon.add(muzzle);

  // Stripes around the barrel for the candy look.
  const stripeMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.45 });
  for (const x of [0.3, 0.95, 1.6]) {
    const stripe = new THREE.Mesh(new THREE.CylinderGeometry(0.58, 0.71, 0.14, 24), stripeMat);
    stripe.rotation.z = Math.PI / 2;
    stripe.position.x = x;
    cannon.add(stripe);
  }

  // Pivot base on top of the platform.
  const swivel = new THREE.Mesh(
    new THREE.CylinderGeometry(0.6, 0.7, 0.4, 18),
    new THREE.MeshStandardMaterial({ color: 0x4a4a5a, roughness: 0.55 }),
  );
  swivel.position.y = -0.15;
  cannon.add(swivel);

  group.add(cannon);

  // Muzzle flash — a small additive plane that we toggle visible during fire.
  const flashMat = new THREE.MeshBasicMaterial({
    color: 0xffe580,
    transparent: true,
    opacity: 0.0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const flash = new THREE.Mesh(new THREE.SphereGeometry(0.95, 18, 14), flashMat);
  flash.position.set(2.95, 0, 0);
  cannon.add(flash);

  return { group, cannon, flash, flashMat };
}

/** Where the hit-parade cannons live along the ramp. Each entry produces
 *  TWO cannons (left + right of the path) at the given arc-length, both
 *  visually and as ball-spawn anchors. Indices are stable so the spawner
 *  can derive deterministic ball ids from them. */
export function hitParadeCannonAnchors(courseLength: number): Array<{ s: number; height: number }> {
  // One pair only — at the top of the ramp. The mid-ramp pair was
  // dropped to halve overall ball density (playtest: level 1 was too
  // punishing for kids).
  return [
    { s: courseLength - 28, height: 6.5 },   // top of mountain
  ];
}

/** Fire cadence shared by visuals + ball respawn so muzzle flashes
 *  line up with new balls coming out of the cannon. */
export const HIT_PARADE_CANNON_PERIOD_MS = 4000;
/** ±15° (= 30° total sweep) so balls leave at a different yaw each shot. */
export const HIT_PARADE_CANNON_SWEEP_RAD = (30 * Math.PI) / 180 / 2;

/**
 * Build cannons on both sides of the path at every anchor.
 */
export function buildHitParadeCannons(path: Path, courseLength: number, clockOrigin?: number): {
  group: THREE.Group;
  tick: (t: number) => void;
} {
  // Same Date.now() epoch as the moving platform — all visual cannon firing
  // and recoil syncs across MP clients.
  const origin = clockOrigin ?? Date.now();
  const group = new THREE.Group();
  const cannons: Cannon[] = [];

  const anchors = hitParadeCannonAnchors(courseLength);
  let bobIdx = 0;
  for (const anchor of anchors) {
    const baseHeight = path.heightAt(anchor.s) + anchor.height;
    for (const side of [-1, 1] as const) {
      const sideOffset = 9.5 * side;
      const pos = path.positionAt(anchor.s, sideOffset);
      pos.y = baseHeight;
      const built = makeCannon(side);
      built.group.position.copy(pos);
      built.group.rotation.y = path.yawAt(anchor.s);
      built.cannon.rotation.y = side === -1 ? -Math.PI / 2.4 : -Math.PI + Math.PI / 2.4;
      group.add(built.group);
      cannons.push({
        group: built.group,
        basePos: pos.clone(),
        baseYaw: path.yawAt(anchor.s),
        cannon: built.cannon,
        flash: built.flash,
        flashMat: built.flashMat,
        nextFireAt: 600 + (side === 1 ? HIT_PARADE_CANNON_PERIOD_MS / 2 : 0),
        recoilAt: 0,
        bobPhase: (bobIdx++ * 0.7) % (Math.PI * 2),
        side,
      });
    }
  }

  const tick = (_t: number) => {
    const t = Date.now() - origin;
    for (const c of cannons) {
      // Idle bob.
      const bob = Math.sin(t * 0.0026 + c.bobPhase) * 0.32;
      c.group.position.y = c.basePos.y + bob;
      c.group.rotation.z = Math.sin(t * 0.0022 + c.bobPhase) * 0.05;

      // Cannon sweep — ±15° back and forth over one full firing cycle so
      // each shot leaves the barrel pointing at a different angle. Aim
      // around the same down-slope baseline as before.
      const baseAim = c.side === -1 ? -Math.PI / 2.4 : -Math.PI + Math.PI / 2.4;
      const sweepPhase = (t / HIT_PARADE_CANNON_PERIOD_MS) * Math.PI * 2;
      c.cannon.rotation.y = baseAim + Math.sin(sweepPhase + c.bobPhase) * HIT_PARADE_CANNON_SWEEP_RAD;

      // Periodic fire — 4000ms interval, side-staggered.
      const cannonPeriod = HIT_PARADE_CANNON_PERIOD_MS;
      const sideOffset = c.side === 1 ? cannonPeriod / 2 : 0;
      const firePhase = (t + sideOffset) % cannonPeriod;
      if (firePhase < 80) {
        c.recoilAt = t - firePhase;
      }
      const recoil = Math.max(0, 1 - (t - c.recoilAt) / 280);
      c.cannon.position.x = -recoil * 0.18;
      const flashT = Math.max(0, 1 - (t - c.recoilAt) / 200);
      c.flashMat.opacity = flashT * 0.95;
      c.flash.scale.setScalar(1 + (1 - flashT) * 1.2);
    }
  };

  return { group, tick };
}

/**
 * Pink-and-white candy platform that oscillates forward along the path near
 * the start. Players standing on top are carried by the platform's current
 * velocity (handled in Game). Doesn't gate progress — players can walk past
 * it — but it's a fun first interaction that introduces the mechanic.
 */
/**
 * `clockOrigin` is the Date.now() reference all clients share so the
 * platform's oscillation is *identical* on every device — critical for
 * multiplayer, where each client would otherwise compute a different
 * platform position from its own page-load `performance.now()` and players
 * would see each other land/fall in the wrong place. Solo mode passes
 * Date.now() at run start (any value is fine since there are no peers).
 */
export function buildHitParadeStartPlatform(path: Path, clockOrigin?: number): MovingPlatform {
  const group = new THREE.Group();
  // Wider, deeper platform so it's an easier target to jump onto. The
  // half-width spans most of the path so kids don't need pixel-perfect aim.
  const halfWidth = 4.6;
  const halfDepth = 3.0;
  const thickness = 0.55;

  const topMat = new THREE.MeshStandardMaterial({ color: 0xff5fa7, roughness: 0.4, metalness: 0.04 });
  const stripeMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.5 });
  const base = new THREE.Mesh(new SoftBox(halfWidth * 2, thickness, halfDepth * 2), topMat);
  base.castShadow = true;
  base.receiveShadow = true;
  group.add(base);

  // Candy stripes on the top surface for that Fall Guys feel. Stripe count
  // and spacing scale with the platform depth so a wider/longer platform
  // still looks proportionally striped.
  const stripeCount = Math.max(3, Math.floor(halfDepth * 2 / 1.0));
  const stripeSpacing = (halfDepth * 2 * 0.85) / Math.max(1, stripeCount - 1);
  for (let i = 0; i < stripeCount; i++) {
    const stripe = new THREE.Mesh(new SoftBox(halfWidth * 2 * 0.95, 0.04, 0.55), stripeMat);
    const z = -((stripeCount - 1) / 2) * stripeSpacing + i * stripeSpacing;
    stripe.position.set(0, thickness / 2 + 0.001, z);
    group.add(stripe);
  }

  // Glow underside hover effect.
  const glow = new THREE.Mesh(
    new THREE.CylinderGeometry(2.6, 3.4, 0.4, 24, 1, true),
    new THREE.MeshBasicMaterial({ color: 0xfff3a8, transparent: true, opacity: 0.45 }),
  );
  glow.position.y = -thickness;
  group.add(glow);

  // Movement: oscillate across the gap in the path. The player must time
  // their jump onto the platform when it's at one edge of the gap, then
  // ride it across. Endpoints overlap the floor on each side slightly so
  // there's a clear landing zone.
  const sStart = 22;
  const sEnd = 50;
  // Slower oscillation — gives the player time to time their jump.
  const periodMs = 14000;
  // Shared Date.now() epoch makes the platform position identical across
  // clients. Fallback is Date.now() at first tick — fine for solo play.
  const origin = clockOrigin ?? Date.now();
  let lastS = sStart;

  const tick = (_dt: number) => {
    const t = (Date.now() - origin) / periodMs;
    // Half-sine for smooth ease in/out, ping-pong back and forth.
    const phase = (t % 1) * Math.PI * 2;
    const eased = (1 - Math.cos(phase)) / 2; // 0..1..0
    const s = sStart + (sEnd - sStart) * eased;
    const groundY = path.heightAt(s);
    const topY = groundY + thickness;
    const pos = path.positionAt(s, 0);
    group.position.set(pos.x, pos.y + thickness / 2, pos.z);
    group.rotation.y = path.yawAt(s);
    glow.rotation.y += _dt * 1.5;
    const velocityS = (s - lastS) / Math.max(_dt, 0.001);
    lastS = s;
    return { s, topY, halfDepth, halfWidth, halfHeight: thickness / 2, velocityS };
  };

  return { group, tick };
}
